/**
 * Tiny deterministic PRNG (mulberry32).
 *
 * Determinism matters: seeding the generator the same way reproduces the exact
 * same number sequence every run — which is what keeps the Playwright tests
 * (and any seeded gameplay) reliable.
 */
import { mulberry32 } from './util';

export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  /** Re-seed the generator — used by `Game.reset()` to make runs reproducible. */
  reseed(seed: number): void {
    this.state = seed >>> 0;
  }

  /**
   * Snapshot of the generator's internal state — captured by the run-save blob
   * so a resumed run continues the *same* PRNG sequence, not just the original
   * seed. Round-trips through {@link setState}. The integer encoding is opaque
   * to callers; only `getState` / `setState` are stable contracts.
   */
  getState(): number {
    return this.state >>> 0;
  }

  /** Restore a prior state captured by {@link getState}. */
  setState(state: number): void {
    this.state = state >>> 0;
  }

  /** Next pseudo-random float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    return mulberry32(this.state);
  }

  /** Next pseudo-random float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }
}
